import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import * as styles from './styles';
import useReportExporter from './useReportExporter';
import moment from 'moment';

import type {
  DateRange,
  PageableContent,
  Sort,
  SortOrder,
  UUID,
  Vehicle,
  VehicleGroup,
} from '@cobliteam/common-types';

import {
  type Driver,
  isWebDataError,
  isWebDataFetching,
  isWebDataSuccess,
  type SettingsType,
  type UserAlert,
  type UserAlertSettings,
  type UserAlertsFilters,
  type WebData,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';
import MedianSpeedBanner from '@cobliteam/speeding/src/components/SpeedingPage/components/MedianSpeedBanner';

import { useVehicleGroup } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

import {
  AccessDeniedPanel,
  applyCustomStyles,
  Divider,
  Heading1,
  HintPopup,
  InfoIcon,
  Label,
  Loading,
  NoConnectionIcon,
  PeriodSelection,
  Placeholder,
  PlaceholderDateRange,
  TableLoading,
} from '@cobliteam/base-components';
import withPermission from '@cobliteam/base-components/src/PermissionsGate/PermissionsGate';

import { Button } from '@cobliteam/ui/components';

import { SaveAltRounded } from '@material-ui/icons';

import UserAlertsPermission from '../../permissions';
import { AlertSettingsCTA } from '../AlertSettingsCTA/AlertSettingsCTA';
import { FiltersSection } from '../FiltersSection/FiltersSection';
import { InfoModal } from '../InfoModal/InfoModal';
import NewTriggersFeatureInfoBanner from '../NewTriggersFeatureInfoBanner';
import { UserAlertsPaper } from '../UserAlertsPaper/UserAlertsPaper';
import { UserAlertsTable } from '../UserAlertsTable/UserAlertsTable';

export interface UserAlertsPageProps {
  userAlertsSettingsWebData: WebData<UserAlertSettings[]>;
  userAlertsWebData: WebData<PageableContent<UserAlert>>;

  driversById: Map<UUID, Driver>;
  vehiclesById: Map<UUID, Vehicle>;

  loadOnChanges: (
    period: DateRange,
    page: number,
    size: number,
    filters?: UserAlertsFilters,
    sort?: Sort
  ) => void;
  loadUserAlertsSettings: () => void;
}

export const AlertHintPopup = applyCustomStyles(HintPopup);
const TooltipLabel = applyCustomStyles(Label);
const DEFAULT_PERIOD: DateRange = {
  start: moment().startOf('day').subtract(1, 'week').valueOf(),
  end: moment().valueOf(),
};

const UserAlertsPage: React.FC<UserAlertsPageProps> = ({
  userAlertsSettingsWebData,
  userAlertsWebData,
  driversById,
  vehiclesById,
  loadOnChanges,
  loadUserAlertsSettings,
}) => {
  const { generateReport, isLoadingReport } = useReportExporter();

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const [invalidSelectedPeriod, setInvalidSelectedPeriod] = useState(false);

  const [wantedAlertTypes, setWantedAlertTypes] = useState<SettingsType[]>([]);
  const [wantedGroups, setWantedGroups] = useState<VehicleGroup[]>([]);
  const [wantedVehicleIds, setWantedVehicleIds] = useState<UUID[]>([]);
  const [sort, setSort] = useState<Sort | undefined>(undefined);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [exceedsMaxNumberOfAlerts, setExceedsMaxNumberOfAlerts] =
    useState(false);

  const { maxElementsForAlertReport } = useFeatureFlags();

  const { getVehicleGroups } = useVehicleGroup();
  const {
    data: vehicleGroups,
    isSuccess: isVehicleGroupsSuccess,
    refetch: fetchVehicleGroups,
  } = getVehicleGroups();

  const vehicleGroupsById: Map<UUID, VehicleGroup> = useMemo(() => {
    if (isVehicleGroupsSuccess) {
      return new Map(vehicleGroups.map((group) => [group.id, group]));
    }
    return new Map();
  }, [isVehicleGroupsSuccess, vehicleGroups]);

  const condenseFilters = (): UserAlertsFilters => {
    const alertTypes = !wantedAlertTypes.length ? undefined : wantedAlertTypes;
    const vehicleGroupIds = !wantedGroups.length
      ? undefined
      : wantedGroups.map((_) => _.id);
    const vehicleIds = !wantedVehicleIds.length ? undefined : wantedVehicleIds;

    return {
      wantedAlertTypes: alertTypes,
      wantedGroups: vehicleGroupIds,
      wantedVehicleIds: vehicleIds,
    };
  };

  const handleReloadOnError = () => {
    Analytics.track('Clicked To Reload User Alerts History On Error');
    loadOnChanges(period, currentPage, rowsPerPage, condenseFilters(), sort);
  };

  const onTooltipClick = () => {
    setIsInfoModalOpen(true);
    Analytics.track('Clicked On User Alerts Header Tooltip');
  };

  const handlePeriodChange = (newPeriod: DateRange) => {
    const daysRange = moment(newPeriod.end).diff(
      moment(newPeriod.start),
      'days'
    );
    Analytics.track('User Alerts Period Selection Changed', {
      range: daysRange,
    });
    setPeriod(newPeriod);
    setInvalidSelectedPeriod(false);
  };

  const handleAlertTypeFilterApply = (newAlertTypes: SettingsType[]) => {
    Analytics.track('Filtered User Alerts History View By Alert Types', {
      newAlertTypes,
    });
    setWantedAlertTypes(newAlertTypes);
  };

  const handleGroupFilterApply = (newVehicleGroups: VehicleGroup[]) => {
    Analytics.track('Filtered User Alerts History View By Vehicle Groups', {
      numberOfGroups: newVehicleGroups.length,
    });
    setWantedGroups(newVehicleGroups);
    setWantedVehicleIds([]);
  };

  const handleVehicleFilterApply = (newVehicleIds: UUID[]) => {
    Analytics.track('Filtered User Alerts History View By Vehicles', {
      numberOfVehicles: newVehicleIds.length,
    });
    setWantedVehicleIds(newVehicleIds);
  };

  const handlePaginationChange = (newPage: number, newRowsPerPage: number) => {
    Analytics.track('Changed User Alerts Pagination', {
      newPage,
      newRowsPerPage,
    });
    setCurrentPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleRequestSort = (column: string, order: SortOrder) => {
    Analytics.track('Sorted User Alerts Table', { column, order });
    setSort({ column, order });
  };

  const handleExport = () => {
    Analytics.track('Exported User Alerts History Report');
    generateReport(
      period,
      driversById,
      vehiclesById,
      vehicleGroupsById,
      condenseFilters()
    );
  };

  const exportButton = (
    <Button
      startIcon={<SaveAltRounded />}
      disabled={
        invalidSelectedPeriod ||
        !isWebDataSuccess(userAlertsWebData) ||
        exceedsMaxNumberOfAlerts
      }
      loading={isLoadingReport}
      className={styles.button}
      onClick={handleExport}
    >
      Exportar
    </Button>
  );

  const hintTitle = (
    <div className={styles.hintTitle}>
      <span>O período selecionado tem mais eventos do que o limite</span>
      <br />
      <span>
        de exportação de {maxElementsForAlertReport / 1000} mil eventos, tente
        reduzir o período{' '}
      </span>
      <br />
      <span>ou aplicar filtros</span>
    </div>
  );

  const renderAlertsHistory = () => {
    if (isWebDataError(userAlertsWebData)) {
      return (
        <Placeholder
          className={styles.placeholderWithTextButton}
          text="Ops! Ocorreu um erro no sistema."
          Icon={NoConnectionIcon}
        >
          <Button variant="text" onClick={handleReloadOnError}>
            Recarregar
          </Button>
        </Placeholder>
      );
    }
    if (isWebDataSuccess(userAlertsWebData)) {
      const userAlertsPage = userAlertsWebData.data;

      return (
        <>
          <div className={styles.filters}>
            <FiltersSection
              vehicleGroups={vehicleGroups || []}
              vehicles={Array.from(vehiclesById.values())}
              wantedAlertTypes={wantedAlertTypes}
              wantedGroups={wantedGroups}
              wantedVehicleIds={wantedVehicleIds}
              onAlertFilterChanged={handleAlertTypeFilterApply}
              onVehicleFilterChanged={handleVehicleFilterApply}
              onVehicleGroupFilterChanged={handleGroupFilterApply}
            />
            <Divider />
          </div>
          {userAlertsPage.empty ? (
            <Placeholder
              className={styles.placeholderWithoutTextButton}
              text="Nenhum resultado encontrado. Ajuste o período ou filtros para uma nova busca."
              Icon={PlaceholderDateRange}
            />
          ) : (
            <UserAlertsTable
              userAlerts={userAlertsPage.content}
              totalRowsLength={userAlertsPage.totalElements}
              rowsPerPage={userAlertsPage.size}
              currentPage={userAlertsPage.number}
              sort={sort}
              vehiclesById={vehiclesById}
              driversById={driversById}
              groupsById={vehicleGroupsById}
              onPaginationChange={handlePaginationChange}
              onRequestSort={handleRequestSort}
            />
          )}
        </>
      );
    }
    return <TableLoading />;
  };

  useEffect(() => {
    if (
      isWebDataSuccess(userAlertsWebData) &&
      userAlertsWebData.data.totalElements >= maxElementsForAlertReport
    ) {
      Analytics.track('User Alerts History Report Export Blocked', {
        numberOfAlertsExceeded: true,
        numberOfAlerts: userAlertsWebData.data.totalElements,
        maxNumberOfAlerts: maxElementsForAlertReport,
      });

      setExceedsMaxNumberOfAlerts(true);
    } else {
      setExceedsMaxNumberOfAlerts(false);
    }
  }, [
    period,
    wantedAlertTypes,
    wantedGroups,
    wantedVehicleIds,
    userAlertsWebData,
  ]);

  useEffect(() => {
    Analytics.track('User Alerts Page Viewed');
    loadUserAlertsSettings();
    fetchVehicleGroups();
  }, []);

  useEffect(() => {
    if (isWebDataSuccess(userAlertsSettingsWebData)) {
      setCurrentPage(0);
      loadOnChanges(period, 0, rowsPerPage, condenseFilters(), sort);
    }
  }, [
    period,
    rowsPerPage,
    wantedAlertTypes,
    wantedGroups,
    wantedVehicleIds,
    sort,
  ]);

  useEffect(() => {
    loadOnChanges(period, currentPage, rowsPerPage, condenseFilters(), sort);
  }, [currentPage]);

  return (
    <>
      <MedianSpeedBanner bannerPage="alertas" />
      <UserAlertsPaper>
        {isWebDataFetching(userAlertsSettingsWebData) ? (
          <div className={styles.containerLoading}>
            <Loading />
          </div>
        ) : isWebDataSuccess(userAlertsSettingsWebData) &&
          userAlertsSettingsWebData.data.some((_) => _.active) ? (
          <>
            <div className={styles.header}>
              <Heading1>Histórico de alertas</Heading1>
              <TooltipLabel
                icon={<InfoIcon />}
                customStyles={styles.tooltipLabel}
                onClick={onTooltipClick}
              >
                Entenda os cálculos
              </TooltipLabel>
            </div>
            <NewTriggersFeatureInfoBanner />
            <div className={styles.inputSection}>
              <PeriodSelection
                period={period}
                onPeriodChange={handlePeriodChange}
                className={styles.subheader}
                onPeriodInvalid={() => setInvalidSelectedPeriod(true)}
              />
              {(exceedsMaxNumberOfAlerts && (
                <AlertHintPopup
                  customStyles={styles.hintStyles}
                  title={hintTitle}
                  placement="bottom-end"
                  arrow
                  disableFocusListener
                  disableTouchListener
                >
                  <div className={styles.hintButtonWrapper}>{exportButton}</div>
                </AlertHintPopup>
              )) ||
                exportButton}
            </div>
            {renderAlertsHistory()}
          </>
        ) : (
          <AlertSettingsCTA />
        )}

        {isInfoModalOpen && (
          <InfoModal onCancel={() => setIsInfoModalOpen(false)} />
        )}
      </UserAlertsPaper>
    </>
  );
};

export default withPermission(
  UserAlertsPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  UserAlertsPage,
  () => (
    <AccessDeniedPanel pageDescription="configurar notificações e alertas" />
  )
);
